import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Tooltip,
  Badge,
  Divider,
  Stack,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  ToggleButton,
  ToggleButtonGroup,
  Avatar,
  LinearProgress,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Rating,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  FormGroup,
  Snackbar
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Person as PersonIcon,
  AccessTime as TimeIcon,
  TrendingUp as TrendingUpIcon,
  FilterList as FilterIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  Schedule as ScheduleIcon,
  CheckCircleOutline as CheckIcon,
  Error as ErrorIcon,
  Pending as PendingIcon,
  Assignment as AssignmentIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Assessment as AssessmentIcon,
  Gavel as GavelIcon,
  Flag as FlagIcon,
  Block as BlockIcon,
  Send as SendIcon,
  CheckCircle as CompleteIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import { makeApiCall } from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';
import { formatScheduledDate } from '../../utils/dateUtils';
import { useTranslation } from 'react-i18next';

// Postgres numeric columns (overall_rating, AVG(...)) arrive as strings.
// Calling .toFixed() on them crashed the whole page (TKT-464).
const toRating = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const PerformanceReviews = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [view, setView] = useState('reviews'); // 'reviews', 'templates', 'analytics'
  
  // Data states
  const [reviews, setReviews] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [stats, setStats] = useState({});
  
  // Dialog states
  const [openDialog, setOpenDialog] = useState(false);
  const [editingReview, setEditingReview] = useState(null);
  const [selectedReview, setSelectedReview] = useState(null);
  const [openTemplateDialog, setOpenTemplateDialog] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [savingTemplate, setSavingTemplate] = useState(false);
  // TKT-254: MUI confirm modal for deleting a template (no browser confirm()).
  const [deleteTemplateTarget, setDeleteTemplateTarget] = useState(null);
  const [deletingTemplate, setDeletingTemplate] = useState(false);
  
  // Form states
  const [formData, setFormData] = useState({
    employee_id: '',
    template_id: '',
    review_period_start: '',
    review_period_end: '',
    reviewer_id: '',
    overall_rating: 0,
    strengths: '',
    areas_for_improvement: '',
    goals: '',
    status: 'draft'
  });
  
  const [templateFormData, setTemplateFormData] = useState({
    template_name: '',
    description: '',
    review_frequency: 'annual',
    questions: []
  });
  
  // Filter states
  const [filters, setFilters] = useState({
    employee: 'all',
    status: 'all',
    template: 'all',
    period: 'all'
  });
  
  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadReviews(),
        loadTemplates(),
        loadEmployees(),
        loadStats()
      ]);
    } catch (err) {
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const loadReviews = async () => {
    try {
      const response = await makeApiCall('api/reviews');
      if (response.ok) {
        const data = await response.json();
        setReviews((data.reviews || []).map((r) => ({ ...r, overall_rating: toRating(r.overall_rating) })));
      } else {
        setReviews([]);
        setError(t('performanceReviews.loadFailed'));
      }
    } catch (err) {
      console.error('Error loading reviews:', err);
      setReviews([]);
      setError(t('performanceReviews.loadFailed'));
    }
  };

  const loadTemplates = async () => {
    try {
      const response = await makeApiCall('api/reviews/templates');
      if (response.ok) {
        const data = await response.json();
        setTemplates(data.templates || []);
      } else {
        setTemplates([]);
        setError(t('performanceReviews.loadFailed'));
      }
    } catch (err) {
      console.error('Error loading templates:', err);
      setTemplates([]);
      setError(t('performanceReviews.loadFailed'));
    }
  };

  const loadEmployees = async () => {
    try {
      // TKT-270: /api/employees paginates at 10 by default, so the
      // employee dropdown on review templates only showed the first page.
      const response = await makeApiCall('api/employees?page=1&limit=1000');
      if (response.ok) {
        const data = await response.json();
        setEmployees(data.employees || []);
      }
    } catch (err) {
      console.error('Error loading employees:', err);
    }
  };

  const loadStats = async () => {
    try {
      const response = await makeApiCall('api/reviews/stats/overview');
      if (response.ok) {
        const data = await response.json();
        const loaded = data.stats || {};
        setStats({ ...loaded, average_rating: toRating(loaded.average_rating) });
      } else {
        setStats({});
      }
    } catch (err) {
      console.error('Error loading stats:', err);
      setStats({});
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'success';
      case 'draft': return 'warning';
      case 'acknowledged': return 'info';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return <CompleteIcon />;
      case 'draft': return <EditIcon />;
      case 'acknowledged': return <CheckIcon />;
      default: return <PendingIcon />;
    }
  };

  const getRatingColor = (rating) => {
    if (rating >= 4.5) return 'success';
    if (rating >= 3.5) return 'warning';
    if (rating >= 2.5) return 'error';
    return 'default';
  };

  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;
    
    for (let i = 0; i < 5; i++) {
      if (i < fullStars) {
        stars.push(<StarIcon key={i} color="primary" />);
      } else if (i === fullStars && hasHalfStar) {
        stars.push(<StarIcon key={i} color="primary" sx={{ opacity: 0.5 }} />);
      } else {
        stars.push(<StarBorderIcon key={i} color="disabled" />);
      }
    }
    return stars;
  };

  const handleCreateReview = () => {
    setEditingReview(null);
    setFormData({
      employee_id: '',
      template_id: '',
      review_period_start: '',
      review_period_end: '',
      reviewer_id: user.id,
      overall_rating: 0,
      strengths: '',
      areas_for_improvement: '',
      goals: '',
      status: 'draft'
    });
    setOpenDialog(true);
  };

  const handleEditReview = (review) => {
    setEditingReview(review);
    setFormData({
      employee_id: review.employee_id,
      template_id: review.template_id,
      review_period_start: review.review_period_start,
      review_period_end: review.review_period_end,
      reviewer_id: review.reviewer_id,
      overall_rating: toRating(review.overall_rating),
      strengths: review.strengths,
      areas_for_improvement: review.areas_for_improvement,
      goals: review.goals,
      status: review.status
    });
    setOpenDialog(true);
  };

  const handleViewReview = (review) => {
    setSelectedReview(review);
  };

  const handleSaveReview = async () => {
    // FB-067: Validate required fields client-side BEFORE submitting
    const missing = [];
    if (!formData.employee_id) missing.push('Employee');
    // TKT-254: Template is optional for a draft review (backend accepts a null
    // template_id). Crews can pick a template now that the management UI exists,
    // but a partial review can still be drafted without one.
    if (!formData.review_period_start) missing.push('Review Period Start');
    if (!formData.review_period_end) missing.push('Review Period End');
    if (missing.length > 0) {
      setError(`Please fill in the following required fields: ${missing.join(', ')}`);
      return;
    }

    const reviewerId = formData.reviewer_id || user?.id;
    if (!reviewerId) {
      setError('Unable to determine reviewer. Please refresh the page and sign in again.');
      return;
    }

    try {
      let savedReviewId = editingReview?.id;

      if (editingReview) {
        // Update path: PUT supports rating/strengths/areas/goals/status
        const putBody = {
          overall_rating: formData.overall_rating,
          strengths: formData.strengths,
          areas_for_improvement: formData.areas_for_improvement,
          goals: formData.goals,
          status: formData.status
        };
        const response = await makeApiCall(`api/reviews/${editingReview.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(putBody)
        });
        if (!response.ok) {
          const data = await response.json().catch(() => ({}));
          throw new Error(data.error || data.message || `Save failed (HTTP ${response.status})`);
        }
      } else {
        // Create path: POST only accepts the 5 metadata fields, then PUT the rest
        const postBody = {
          employee_id: formData.employee_id,
          template_id: formData.template_id,
          review_period_start: formData.review_period_start,
          review_period_end: formData.review_period_end,
          reviewer_id: reviewerId
        };
        const createResponse = await makeApiCall('api/reviews', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(postBody)
        });
        if (!createResponse.ok) {
          const data = await createResponse.json().catch(() => ({}));
          throw new Error(data.error || data.message || `Create failed (HTTP ${createResponse.status})`);
        }
        const createData = await createResponse.json();
        savedReviewId = createData.review?.id;

        // If we have detail fields, follow up with a PUT so they actually save
        const hasDetail =
          (formData.overall_rating && formData.overall_rating > 0) ||
          formData.strengths ||
          formData.areas_for_improvement ||
          formData.goals ||
          (formData.status && formData.status !== 'draft');

        if (savedReviewId && hasDetail) {
          const putBody = {
            overall_rating: formData.overall_rating,
            strengths: formData.strengths,
            areas_for_improvement: formData.areas_for_improvement,
            goals: formData.goals,
            status: formData.status
          };
          const putResponse = await makeApiCall(`api/reviews/${savedReviewId}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(putBody)
          });
          if (!putResponse.ok) {
            const data = await putResponse.json().catch(() => ({}));
            throw new Error(data.error || data.message || `Saved review but could not save details (HTTP ${putResponse.status})`);
          }
        }
      }

      setSuccess(editingReview ? 'Review updated successfully' : 'Review created successfully');
      setOpenDialog(false);
      loadReviews();
    } catch (err) {
      console.error('Error saving review:', err);
      setError(err.message || 'Failed to save review');
    }
  };

  const handleCreateTemplate = () => {
    setEditingTemplate(null);
    setTemplateFormData({
      template_name: '',
      description: '',
      review_frequency: 'annual',
      questions: []
    });
    setOpenTemplateDialog(true);
  };

  const handleEditTemplate = async (template) => {
    setEditingTemplate(template);
    // The list endpoint doesn't carry questions, so fetch the full template
    // (GET /templates/:id) to populate the question builder for editing.
    let questions = template.questions || [];
    try {
      const response = await makeApiCall(`api/reviews/templates/${template.id}`);
      if (response.ok) {
        const data = await response.json();
        questions = (data.questions || []).map((q) => ({
          question_text: q.question_text,
          question_type: q.question_type || 'text',
          is_required: q.is_required !== false,
        }));
      }
    } catch (err) {
      console.error('Error loading template questions:', err);
    }
    setTemplateFormData({
      template_name: template.template_name || '',
      description: template.description || '',
      review_frequency: template.review_frequency || 'annual',
      questions,
    });
    setOpenTemplateDialog(true);
  };

  // TKT-254: question-builder helpers for the template dialog.
  const addTemplateQuestion = () => {
    setTemplateFormData((prev) => ({
      ...prev,
      questions: [
        ...prev.questions,
        { question_text: '', question_type: 'rating', is_required: true },
      ],
    }));
  };

  const updateTemplateQuestion = (index, field, value) => {
    setTemplateFormData((prev) => ({
      ...prev,
      questions: prev.questions.map((q, i) => (i === index ? { ...q, [field]: value } : q)),
    }));
  };

  const removeTemplateQuestion = (index) => {
    setTemplateFormData((prev) => ({
      ...prev,
      questions: prev.questions.filter((_, i) => i !== index),
    }));
  };

  const handleSaveTemplate = async () => {
    const name = (templateFormData.template_name || '').trim();
    if (!name) {
      setError('Template name is required.');
      return;
    }
    const questions = (templateFormData.questions || [])
      .map((q, i) => ({
        question_text: (q.question_text || '').trim(),
        question_type: q.question_type || 'text',
        display_order: i + 1,
        is_required: q.is_required !== false,
      }))
      .filter((q) => q.question_text);
    if (questions.length === 0) {
      setError('Add at least one question with text before saving the template.');
      return;
    }

    const payload = {
      template_name: name,
      description: templateFormData.description || '',
      review_frequency: templateFormData.review_frequency || 'annual',
      questions,
    };

    try {
      setSavingTemplate(true);
      const endpoint = editingTemplate
        ? `api/reviews/templates/${editingTemplate.id}`
        : 'api/reviews/templates';
      const response = await makeApiCall(endpoint, {
        method: editingTemplate ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || `Save failed (HTTP ${response.status})`);
      }
      setSuccess(editingTemplate ? 'Template updated successfully.' : 'Template created successfully.');
      setOpenTemplateDialog(false);
      setEditingTemplate(null);
      await loadTemplates();
    } catch (err) {
      setError(err.message || 'Failed to save template.');
    } finally {
      setSavingTemplate(false);
    }
  };

  const handleConfirmDeleteTemplate = async () => {
    if (!deleteTemplateTarget) return;
    try {
      setDeletingTemplate(true);
      const response = await makeApiCall(`api/reviews/templates/${deleteTemplateTarget.id}`, {
        method: 'DELETE',
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || `Delete failed (HTTP ${response.status})`);
      }
      setSuccess('Template deleted.');
      setDeleteTemplateTarget(null);
      await loadTemplates();
    } catch (err) {
      setError(err.message || 'Failed to delete template.');
    } finally {
      setDeletingTemplate(false);
    }
  };

  const renderHeader = () => (
    <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
      <Typography variant="h4" component="h1" color="primary" fontWeight="bold">
        Performance Reviews
      </Typography>
      <Box display="flex" alignItems="center" gap={2}>
        <ToggleButtonGroup
          value={view}
          exclusive
          onChange={(e, newView) => newView && setView(newView)}
          size="small"
        >
          <ToggleButton value="reviews">Reviews</ToggleButton>
          <ToggleButton value="templates">Templates</ToggleButton>
          <ToggleButton value="analytics">Analytics</ToggleButton>
        </ToggleButtonGroup>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreateReview}
        >
          New Review
        </Button>
      </Box>
    </Box>
  );

  const renderStats = () => (
    <Grid container spacing={2} sx={{ mb: 3 }}>
      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
        <Card>
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography variant="h4" color="primary" fontWeight="bold">
                  {stats.total_reviews || 0}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Total Reviews
                </Typography>
              </Box>
              <AssessmentIcon color="primary" sx={{ fontSize: 40 }} />
            </Box>
          </CardContent>
        </Card>
      </Grid>
      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
        <Card>
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography variant="h4" color="success.main" fontWeight="bold">
                  {stats.completed_reviews || 0}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Completed
                </Typography>
              </Box>
              <CompleteIcon color="success" sx={{ fontSize: 40 }} />
            </Box>
          </CardContent>
        </Card>
      </Grid>
      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
        <Card>
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography variant="h4" color="warning.main" fontWeight="bold">
                  {stats.draft_reviews || 0}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Drafts
                </Typography>
              </Box>
              <EditIcon color="warning" sx={{ fontSize: 40 }} />
            </Box>
          </CardContent>
        </Card>
      </Grid>
      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
        <Card>
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography variant="h4" color="info.main" fontWeight="bold">
                  {toRating(stats.average_rating).toFixed(1)}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Avg Rating
                </Typography>
              </Box>
              <StarIcon color="info" sx={{ fontSize: 40 }} />
            </Box>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );

  const renderFilters = () => (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Filters
        </Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Employee</InputLabel>
              <Select
                value={filters.employee}
                onChange={(e) => setFilters({ ...filters, employee: e.target.value })}
                label="Employee"
              >
                <MenuItem value="all">All Employees</MenuItem>
                {employees.map((emp) => (
                  <MenuItem key={emp.id} value={emp.id}>
                    {emp.employee_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                label="Status"
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="draft">Draft</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="acknowledged">Acknowledged</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Template</InputLabel>
              <Select
                value={filters.template}
                onChange={(e) => setFilters({ ...filters, template: e.target.value })}
                label="Template"
              >
                <MenuItem value="all">All Templates</MenuItem>
                {templates.map((template) => (
                  <MenuItem key={template.id} value={template.id}>
                    {template.template_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={loadData}
              fullWidth
            >
              Refresh
            </Button>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );

  const renderReviewsList = () => {
    const filteredReviews = reviews.filter(review => {
      if (filters.employee !== 'all' && review.employee_id !== parseInt(filters.employee)) return false;
      if (filters.status !== 'all' && review.status !== filters.status) return false;
      if (filters.template !== 'all' && review.template_id !== parseInt(filters.template)) return false;
      return true;
    });

    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Performance Reviews
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Employee</TableCell>
                  <TableCell>Template</TableCell>
                  <TableCell>Period</TableCell>
                  <TableCell>Rating</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Reviewer</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredReviews
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((review) => (
                    <TableRow key={review.id}>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Avatar sx={{ width: 32, height: 32 }}>
                            {review.employee_name.charAt(0)}
                          </Avatar>
                          {review.employee_name}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={review.template_name}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatScheduledDate(review.review_period_start)} - {formatScheduledDate(review.review_period_end)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          {renderStars(review.overall_rating)}
                          <Typography variant="body2" fontWeight="bold">
                            {review.overall_rating.toFixed(1)}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={getStatusIcon(review.status)}
                          label={review.status.toUpperCase()}
                          color={getStatusColor(review.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{review.reviewer_name}</TableCell>
                      <TableCell>
                        <Box display="flex" gap={1}>
                          <Tooltip title="View Details">
                            <IconButton size="small" onClick={() => handleViewReview(review)}>
                              <ViewIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => handleEditReview(review)}>
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={filteredReviews.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(event, newPage) => setPage(newPage)}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(parseInt(event.target.value, 10));
              setPage(0);
            }}
          />
        </CardContent>
      </Card>
    );
  };

  const renderReviewDialog = () => (
    <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
      <DialogTitle>
        {editingReview ? 'Edit Performance Review' : 'New Performance Review'}
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={3} sx={{ mt: 1 }}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth>
              <InputLabel>Employee</InputLabel>
              <Select
                value={formData.employee_id}
                onChange={(e) => setFormData({ ...formData, employee_id: e.target.value })}
                label="Employee"
              >
                {employees.map((emp) => (
                  <MenuItem key={emp.id} value={emp.id}>
                    {emp.employee_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth>
              <InputLabel>Template</InputLabel>
              <Select
                value={formData.template_id}
                onChange={(e) => setFormData({ ...formData, template_id: e.target.value })}
                label="Template"
              >
                {templates.map((template) => (
                  <MenuItem key={template.id} value={template.id}>
                    {template.template_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              label="Review Period Start"
              type="date"
              value={formData.review_period_start}
              onChange={(e) => setFormData({ ...formData, review_period_start: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              label="Review Period End"
              type="date"
              value={formData.review_period_end}
              onChange={(e) => setFormData({ ...formData, review_period_end: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid size={12}>
            <Typography variant="h6" gutterBottom>
              Overall Rating
            </Typography>
            <Rating
              value={formData.overall_rating}
              onChange={(event, newValue) => setFormData({ ...formData, overall_rating: newValue })}
              precision={0.1}
              size="large"
            />
            <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
              {formData.overall_rating > 0 ? `${formData.overall_rating.toFixed(1)} out of 5` : 'No rating selected'}
            </Typography>
          </Grid>
          <Grid size={12}>
            <TextField
              fullWidth
              label="Strengths"
              multiline
              rows={3}
              value={formData.strengths}
              onChange={(e) => setFormData({ ...formData, strengths: e.target.value })}
              placeholder="Describe the employee's key strengths and accomplishments..."
            />
          </Grid>
          <Grid size={12}>
            <TextField
              fullWidth
              label="Areas for Improvement"
              multiline
              rows={3}
              value={formData.areas_for_improvement}
              onChange={(e) => setFormData({ ...formData, areas_for_improvement: e.target.value })}
              placeholder="Identify specific areas where the employee can improve..."
            />
          </Grid>
          <Grid size={12}>
            <TextField
              fullWidth
              label="Goals for Next Period"
              multiline
              rows={3}
              value={formData.goals}
              onChange={(e) => setFormData({ ...formData, goals: e.target.value })}
              placeholder="Set clear, measurable goals for the next review period..."
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                label="Status"
              >
                <MenuItem value="draft">Draft</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="acknowledged">Acknowledged</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
        <Button variant="contained" onClick={handleSaveReview}>
          {editingReview ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );

  const renderTemplatesList = () => (
    <Card>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">
            Review Templates
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateTemplate}
            size="small"
          >
            New Template
          </Button>
        </Box>
        {templates.length === 0 && (
          <Alert severity="info" sx={{ mb: 2 }}>
            No review templates yet. Click "New Template" to create one (e.g. "Annual Review - Crew") so it appears in the Template dropdown when creating a review.
          </Alert>
        )}
        <Grid container spacing={2}>
          {templates.map((template) => (
            <Grid size={{ xs: 12, md: 6 }} key={template.id}>
              <Card variant="outlined">
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                    <Typography variant="h6">{template.template_name}</Typography>
                    <Box display="flex" gap={1}>
                      <IconButton size="small" aria-label={`Edit ${template.template_name}`} onClick={() => handleEditTemplate(template)}>
                        <EditIcon />
                      </IconButton>
                      <IconButton size="small" aria-label={`Delete ${template.template_name}`} onClick={() => setDeleteTemplateTarget(template)}>
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </Box>
                  <Typography variant="body2" color="textSecondary" paragraph>
                    {template.description}
                  </Typography>
                  {template.review_frequency && (
                    <Chip
                      label={template.review_frequency}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  )}
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    {Number(template.question_count ?? template.questions?.length ?? 0)} questions
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </CardContent>
    </Card>
  );

  // TKT-254: template create/edit dialog with a dynamic question builder.
  const renderTemplateDialog = () => (
    <Dialog open={openTemplateDialog} onClose={() => setOpenTemplateDialog(false)} fullWidth maxWidth="md">
      <DialogTitle>{editingTemplate ? 'Edit Template' : 'New Template'}</DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2} sx={{ mb: 1 }}>
          <Grid size={{ xs: 12, sm: 8 }}>
            <TextField
              fullWidth
              label="Template Name"
              value={templateFormData.template_name}
              onChange={(e) => setTemplateFormData((prev) => ({ ...prev, template_name: e.target.value }))}
              required
              inputProps={{ 'aria-label': 'Template name' }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <FormControl fullWidth>
              <InputLabel id="template-frequency-label">Frequency</InputLabel>
              <Select
                labelId="template-frequency-label"
                label="Frequency"
                value={templateFormData.review_frequency}
                onChange={(e) => setTemplateFormData((prev) => ({ ...prev, review_frequency: e.target.value }))}
              >
                <MenuItem value="annual">Annual</MenuItem>
                <MenuItem value="semi-annual">Semi-annual</MenuItem>
                <MenuItem value="quarterly">Quarterly</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              label="Description"
              value={templateFormData.description}
              onChange={(e) => setTemplateFormData((prev) => ({ ...prev, description: e.target.value }))}
              multiline
              rows={2}
              inputProps={{ 'aria-label': 'Template description' }}
            />
          </Grid>
        </Grid>

        <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mt: 2, mb: 1 }}>
          <Typography variant="subtitle1" fontWeight="bold">Questions</Typography>
          <Button size="small" startIcon={<AddIcon />} onClick={addTemplateQuestion}>
            Add Question
          </Button>
        </Box>

        {templateFormData.questions.length === 0 && (
          <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
            No questions yet. Add at least one question (rating, text, or yes/no).
          </Typography>
        )}

        <Stack spacing={1.5}>
          {templateFormData.questions.map((q, index) => (
            <Paper key={index} variant="outlined" sx={{ p: 1.5 }}>
              <Grid container spacing={1.5} alignItems="center">
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label={`Question ${index + 1}`}
                    value={q.question_text}
                    onChange={(e) => updateTemplateQuestion(index, 'question_text', e.target.value)}
                    inputProps={{ 'aria-label': `Question ${index + 1} text` }}
                  />
                </Grid>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel id={`q-type-${index}`}>Type</InputLabel>
                    <Select
                      labelId={`q-type-${index}`}
                      label="Type"
                      value={q.question_type}
                      onChange={(e) => updateTemplateQuestion(index, 'question_type', e.target.value)}
                    >
                      <MenuItem value="rating">Rating (1-5)</MenuItem>
                      <MenuItem value="text">Text</MenuItem>
                      <MenuItem value="yes_no">Yes / No</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 4, sm: 2 }}>
                  <FormControlLabel
                    control={(
                      <Checkbox
                        checked={q.is_required !== false}
                        onChange={(e) => updateTemplateQuestion(index, 'is_required', e.target.checked)}
                        inputProps={{ 'aria-label': `Question ${index + 1} required` }}
                      />
                    )}
                    label="Req."
                  />
                </Grid>
                <Grid size={{ xs: 2, sm: 1 }} sx={{ textAlign: 'right' }}>
                  <IconButton
                    size="small"
                    aria-label={`Remove question ${index + 1}`}
                    onClick={() => removeTemplateQuestion(index)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Grid>
              </Grid>
            </Paper>
          ))}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setOpenTemplateDialog(false)}>Cancel</Button>
        <Button variant="contained" onClick={handleSaveTemplate} disabled={savingTemplate}>
          {savingTemplate ? 'Saving...' : (editingTemplate ? 'Update Template' : 'Create Template')}
        </Button>
      </DialogActions>
    </Dialog>
  );

  const renderDeleteTemplateDialog = () => (
    <Dialog open={!!deleteTemplateTarget} onClose={() => setDeleteTemplateTarget(null)} maxWidth="xs" fullWidth>
      <DialogTitle>Delete template?</DialogTitle>
      <DialogContent>
        <Typography variant="body2">
          {deleteTemplateTarget
            ? `"${deleteTemplateTarget.template_name}" will be removed from the template list. Existing reviews that used it are unaffected.`
            : ''}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setDeleteTemplateTarget(null)}>Cancel</Button>
        <Button
          color="error"
          variant="contained"
          onClick={handleConfirmDeleteTemplate}
          disabled={deletingTemplate}
        >
          {deletingTemplate ? 'Deleting...' : 'Delete'}
        </Button>
      </DialogActions>
    </Dialog>
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* TKT-271: these used to be plain page-body Alerts, which are
          rendered behind the New/Edit Template dialog's modal backdrop
          (MUI Dialog z-index 1300 sits above normal page content). A save
          failure while the dialog was open produced an error message the
          office user could never actually see, making it look like
          "Create Template" silently did nothing. Snackbar's z-index
          (1400) renders above open dialogs, so the message is now visible
          regardless of which dialog is open. */}
      <Snackbar
        open={!!error}
        autoHideDuration={8000}
        onClose={() => setError('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setError('')} sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
      <Snackbar
        open={!!success}
        autoHideDuration={5000}
        onClose={() => setSuccess('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setSuccess('')} sx={{ width: '100%' }}>
          {success}
        </Alert>
      </Snackbar>

      {renderHeader()}
      {renderStats()}
      {renderFilters()}
      
      {view === 'reviews' && renderReviewsList()}
      {view === 'templates' && renderTemplatesList()}
      
      {renderReviewDialog()}
      {renderTemplateDialog()}
      {renderDeleteTemplateDialog()}
    </Box>
  );
};

export default PerformanceReviews;
